import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services'
import { queryKeys } from '../lib/queryClient'
import { AlertSettingsItem } from '../common/constants'

/**
 * API response structure for getAlertSettings
 *
 * @remarks
 * The API returns alert settings wrapped in a 'data' property, not 'settings'.
 *
 * Example response: { data: [{ id: 1, enabled: true, ... }, ...] }
 */
export interface GetAlertSettingsResponse {
  data: AlertSettingsItem[]
  [key: string]: any
}

/**
 * React Query hook for fetching alert settings for a device
 *
 * Cache Strategy:
 * - staleTime: 10 minutes (alert settings rarely change)
 * - gcTime: 30 minutes (keep in memory for extended period)
 * - Service Worker: CacheFirst with 30min expiration
 *
 * @param deviceId - Device ID (optional, 0 for global defaults)
 * @param enabled - Whether the query should run (default: true)
 */
export function useAlertSettings(deviceId?: number, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.alertSettings(deviceId),
    queryFn: async () => {
      const result = await api.getAlertSettings(deviceId!)
      return result as GetAlertSettingsResponse
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - alert settings rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes
    enabled: enabled && deviceId !== undefined,
    retry: 2,
    retryDelay: 1000,
  })
}

/**
 * Mutation hook for creating alert settings
 *
 * Automatically invalidates and refetches related alert settings on success
 */
export function useCreateAlertSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (settings: AlertSettingsItem) => {
      return await api.createAlertSettings(settings)
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch alert settings for the device
      queryClient.invalidateQueries({
        queryKey: queryKeys.alertSettings(variables.deviceId),
      })
    },
  })
}

/**
 * Mutation hook for updating alert settings
 *
 * Uses optimistic updates for instant UI feedback
 * Optimistic update is sufficient - no need to refetch (prevents UI flickering)
 */
export function useUpdateAlertSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (settings: AlertSettingsItem) => {
      return await api.updateAlertSettings(settings)
    },
    onMutate: async (newSettings) => {
      const queryKey = queryKeys.alertSettings(newSettings.deviceId)

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey })

      // Snapshot previous value
      const previousSettings = queryClient.getQueryData(queryKey)

      // Optimistically update cache
      queryClient.setQueryData(queryKey, (old: GetAlertSettingsResponse | undefined) => {
        if (!old) return old

        return {
          ...old,
          data: old.data.map((setting) =>
            setting.id === newSettings.id ? { ...setting, ...newSettings } : setting
          ),
        }
      })

      // Return context for rollback on error
      return { previousSettings, queryKey }
    },
    onError: (err, newSettings, context) => {
      // Rollback on error
      if (context?.previousSettings) {
        queryClient.setQueryData(context.queryKey, context.previousSettings)
      }
    },
    // No onSuccess invalidation - optimistic update is sufficient
    // This prevents checkbox from reverting due to refetch
  })
}
